import i18n from "@/i18n";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

enum RelativeDayLabel {
    TODAY = "today",
    YESTERDAY = "yesterday",
}

/**
 * Normalize a date to midnight (removes time component)
 */
const normalizeDate = (date: Date): Date =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate());

/**
 * Format time as HH:MM
 */
const formatTime = (date: Date): { hour: string; minute: string } => ({
    hour: date.getHours().toString().padStart(2, "0"),
    minute: date.getMinutes().toString().padStart(2, "0"),
});

/**
 * Get relative day label (today, yesterday, or null)
 */
const getRelativeDayLabel = (
    date: Date,
    today: Date,
    yesterday: Date,
): RelativeDayLabel | null => {
    const normalized = normalizeDate(date);
    const time = normalized.getTime();

    if (time === today.getTime()) return RelativeDayLabel.TODAY;
    if (time === yesterday.getTime()) return RelativeDayLabel.YESTERDAY;

    return null;
};

/**
 * Format a single date with relative labels (today/yesterday) if applicable
 */
const formatSingleDate = (
    date: Date,
    today: Date,
    yesterday: Date,
    yearDisplay: "numeric" | "2-digit" | undefined,
    monthDisplay: "long" | "2-digit",
    language: string,
    t: (key: string, params?: any) => string,
): string => {
    const relativeDay = getRelativeDayLabel(date, today, yesterday);
    const time = formatTime(date);

    if (relativeDay === RelativeDayLabel.TODAY) {
        return t("global.today", time);
    }

    if (relativeDay === RelativeDayLabel.YESTERDAY) {
        return t("global.yesterday", time);
    }

    return date.toLocaleDateString(language, {
        year: yearDisplay,
        month: monthDisplay,
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

/**
 * Format a date range with appropriate relative labels
 */
const formatDateRange = (
    startDate: Date,
    endDate: Date,
    today: Date,
    yesterday: Date,
    yearDisplay: "numeric" | "2-digit" | undefined,
    monthDisplay: "long" | "2-digit",
    language: string,
    t: (key: string, params?: any) => string,
): string => {
    const separator = " → ";

    const startNormalized = normalizeDate(startDate);
    const endNormalized = normalizeDate(endDate);
    const isSameDay = startNormalized.getTime() === endNormalized.getTime();

    const startRelative = getRelativeDayLabel(startDate, today, yesterday);
    const endRelative = getRelativeDayLabel(endDate, today, yesterday);

    const startTime = formatTime(startDate);
    const endTime = formatTime(endDate);

    const startFormatted = startRelative
        ? t(`global.${startRelative}`, startTime)
        : startDate.toLocaleString(language, {
              year: yearDisplay,
              month: monthDisplay,
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
          });

    if (isSameDay) {
        const endTimeOnly = endDate.toLocaleTimeString(language, {
            hour: "2-digit",
            minute: "2-digit",
        });

        return `${startFormatted}${separator}${endTimeOnly}`;
    }

    const endFormatted = endRelative
        ? t(`global.${endRelative}`, endTime)
        : endDate.toLocaleString(language, {
              month: monthDisplay,
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
          });

    return `${startFormatted}${separator}${endFormatted}`;
};

const useGetStringifiedDateAndHour = () => {
    const { t } = useTranslation();
    const { language } = i18n;

    const getStringifiedDate = useCallback(
        (date: Date, endDate: Date | null = null, fullDisplay = true) => {
            const now = new Date();
            const today = normalizeDate(now);
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);

            const isDifferentYear = date.getFullYear() !== today.getFullYear();
            let yearDisplay: "numeric" | "2-digit" | undefined;
            const monthDisplay = fullDisplay ? "long" : "2-digit";
            if (isDifferentYear) {
                yearDisplay = fullDisplay ? "numeric" : "2-digit";
            }

            if (!endDate) {
                return formatSingleDate(
                    date,
                    today,
                    yesterday,
                    yearDisplay,
                    monthDisplay,
                    language,
                    t,
                );
            }

            return formatDateRange(
                date,
                endDate,
                today,
                yesterday,
                yearDisplay,
                monthDisplay,
                language,
                t,
            );
        },
        [t, language],
    );

    return getStringifiedDate;
};

export default useGetStringifiedDateAndHour;
